import { defineComponent as _defineComponent } from 'vue'
import { resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "onboarding" }

import { computed, onBeforeMount, ref, shallowRef } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'

import { StorageKeys } from '@/helpers/storage.keys'
import { auth } from '@/services/auth'
import { storage } from '@/helpers/storage'
import { Mixpanel } from '@/analytics/mixpanel'
import { maybeSyncAnalyticsIds } from '@/analytics/logic'
import { onboardingPlanCcCaptureV2_20676EVariant } from '@/services/ab/experiments'
import {
  getKnownContentOriginSlug,
  loadInterestBook,
} from '@/helpers/billingSourceBook'
import { Sentry } from '@/services/sentry'
import { clearPlanCardCapture20676 } from '@/models/onboarding.plan-card-capture-20676'
import { isHardGateBilling20516Active } from '@/models/hardGateBilling20516'
import { playerSession } from '@/services/player'

import Questionnaire from '@/app/components/onboarding/pages/Questionnaire.vue'
import SignupWrapper from '@/app/components/onboarding/pages/SignupWrapper.vue'
import Trial from '@/app/components/onboarding/pages/Trial.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'Onboarding',
  setup(__props) {

const router = useRouter()

const pages = shallowRef([
  { name: 'signup', component: SignupWrapper },
  { name: 'questionnaire', component: Questionnaire },
  { name: 'trial', component: Trial },
])

const currentPageIndex = ref(0)
const currentPage = computed(() => {
  return pages.value[currentPageIndex.value]
})

onBeforeMount(async () => {
  if (auth.loggedIn() && !auth.isTemporaryUser()) {
    // Skip the onboarding if the user is logged in and
    // doesn't have a position saved (we clear the position
    // when the user has finished the questionnaire).
    if (!storage.getItem(StorageKeys.questionnairePosition)) {
      router.replace({ name: 'discover' })
      return
    }

    // Skip to the questionnaire if the user is logged in
    // and has a saved position.
    currentPageIndex.value = pages.value.findIndex(
      (page) => page.name === 'questionnaire',
    )
  }

  trackPageView()
})

async function nextPage(): Promise<void> {
  if (currentPageIndex.value + 1 < pages.value.length) {
    currentPageIndex.value++
    await maybeSyncAnalyticsIds()
    trackPageView()
    return
  }

  const finalRoute = await getFinalOnboardingRoute()

  // Prepare the First Guide popup.
  // For variant B of #20676 we send users who started a paid trial and for whom we
  // resolved a "content origin" (book they came from) straight to
  // (see getFinalOnboardingRoute below) the book of interest.
  // Showing the popup for such users doesn't make sense, so we disable it.
  const shouldSkipFirstGuide =
    onboardingPlanCcCaptureV2_20676EVariant() === 'B' &&
    typeof finalRoute !== 'string' &&
    'name' in finalRoute &&
    finalRoute.name === 'book.page'
  // The first-guide popup opens a book. A gated free user would bounce
  // straight back to billing, so don't offer it.
  if (!shouldSkipFirstGuide && !isHardGateBilling20516Active()) {
    storage.setItem(StorageKeys.offerGuide, 'true')
  }

  Mixpanel.trackOnboardingComplete()

  // Cleanup billing plan selection and title for the onboarding plan credit card
  // capture experiment (#20676).
  clearPlanCardCapture20676()

  if (router.resolve(finalRoute).meta.isDocContentPage) {
    playerSession.value.setNextArrival('redirect')
  }
  router.replace(finalRoute)
}

/**
 * Resolve the end-of-onboarding destination for
 * exp_onboarding_plan_cc_capture_v2_20676 (#20676).
 *
 * We re-check everything from reliable sources instead of passing the whole
 * state through the Questionnaire:
 *   - `sf_first_page` from local storage
 *   - the locally stored user's plan status from `user` object.
 */
async function getFinalOnboardingRoute(): Promise<RouteLocationRaw> {
  if (onboardingPlanCcCaptureV2_20676EVariant() !== 'B') {
    return { name: 'billing' }
  }

  // Free users always land on the billing page, so we don't attempt resolving the
  // content origin for them (again).
  if (auth.isFreePlan()) {
    return { name: 'billing' }
  }

  const slug = getKnownContentOriginSlug()
  if (slug) {
    try {
      await loadInterestBook(slug)
      return { name: 'book.page', params: { url_slug: slug } }
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag('experiment', 'exp_onboarding_plan_cc_capture_v2_20676')
        scope.setExtra('slug', slug)
        Sentry.captureMessage(
          'exp_onboarding_plan_cc_capture_v2_20676: content origin unresolvable',
        )
      })
      return { name: 'billing' }
    }
  }

  return { name: 'discover' }
}

function trackPageView(): void {
  // The questionnaire page has its own logic
  // for tracking page views (because it has
  // many sub pages).
  if (currentPage.value.name === 'questionnaire') {
    return
  }

  Mixpanel.trackOnboardingPageView(currentPage.value.name)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_Transition, {
      name: "fade",
      mode: "out-in"
    }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(_resolveDynamicComponent(currentPage.value.component), { nextOnboardingPage: nextPage }))
      ]),
      _: 1
    })
  ]))
}
}

})